<template>
  <div>
    <div class="feed-item" v-for="item in feed" :key="item.id">
      <b-row>
        <b-col class="item-header">
          <router-link class="who" :to="artistLink(item)"><img :src="getArtist(item).avatar_image"></router-link>
          <div class="meta">
            <div class="when">{{ formatDate(item) }}</div>
            <div class="headline">
              <router-link class="who" :to="artistLink(item)">{{ getArtist(item).name }}</router-link>
              <span class="what">
                {{ formatWhat(item) }} {{ formatVisibility(item) }}
              </span>
            </div>
          </div>
        </b-col>
        <b-col cols="1" class="text-right">
          <Report :update="item" label="Report post" class="report-link">
            <LinkButton v-if="canManage" @click="deleteUpdate(item)">Delete post</LinkButton>
          </Report>
        </b-col>
      </b-row>

      <b-row v-if="itemType(item) === 'text'">
        <b-col>
          <p class="msg" v-html="formatText(item.text)"></p>
        </b-col>
      </b-row>

      <div v-if="itemType(item) === 'video'">
        <b-row class="pb-2">
          <b-col class="player-col" cols="12" lg="9">
            <div v-if="currentVideo === item.song">
              <Player :video="item.song" :once="true" :autoplay="true" />
            </div>
            <div v-else>
              <img :src="getThumbnail(item)">
              <font-awesome-icon icon="play" class="play" @click="playVideo(item)" />
            </div>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="12" lg="9">
            <VideoInfo :video="item.song" :hideComments="true" />
          </b-col>
        </b-row>
      </div>

      <div v-if="itemType(item) === 'live'">
        <b-row>
          <b-col cols="12" lg="9">
            <BigEventCard :event="item.live_event" />
          </b-col>
        </b-row>
      </div>

      <div v-if="itemType(item) === 'album'">
        <b-row>
          <b-col cols="12" sm="8">
            <AlbumCard :album="item.album" :showArtist="true" :largeCover="true" />
          </b-col>
          <b-col class="text-right album-actions" v-if="item.album.songs.length">
            <Pay :album="item.album"></Pay>
            <br />
            <b-button variant="secondary" class="play-album" :to="{name: 'album-detail', params: {id: item.album.id}}">
              <font-awesome-icon icon="play"/><span class="label"> &nbsp; play</span>
            </b-button>
          </b-col>
        </b-row>
      </div>
      <b-row>
        <b-col class="comments">
          <Comments :update="item" :small="true" :canManage="canManage" :collapsed="!canManage" />
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<style scoped lang="scss">
  @import '@/assets/custom.scss';
  @import '~bootstrap/scss/mixins/_breakpoints.scss';

  .feed-item {
    margin-top: 0.5em;
    margin-bottom: 1em;
    border-top: 1px solid $warm-grey;
    padding-top: 1em;
    margin-left: .6em;
    margin-right: .6em;

    &:first-child {
      border-top: 0;
    }

    .item-header {
      display: flex;
      margin-bottom: 0.5em;
      font-size: 0.8em;

      img {
        width: 40px;
        height: 40px;
        border-radius: 100%;
        margin-right: 10px;
      }

      .when {
        color: $steel;
      }

      .who {
        font-weight: bold;
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }
    }

    p.msg {
      padding-left: 50px;

      &:last-child {
        margin-bottom: 0.5em;
      }
    }

    .player-col {
      position: relative;

      img {
        background: black;
        min-height: 72px;
        width: 100%;
      }

      .play {
        font-size: 72px;
        color: $darkish-pink;
        position: absolute;
        top: 50%;
        left: 50%;
        cursor: pointer;
        transform: translate(-50%, -50%);
      }
    }

    .play-album {
      margin-top: 0.5em;
      padding-left: 2.7em;
      padding-right: 2.7em;
      margin-right: 4px;
      @include media-breakpoint-down(sm) {
        padding-left: 1.1em;
        padding-right: 1.1em;
        .label {
          display: none;
        }
      }
    }

    .comments {
      padding-left: 30px;
      font-size: 0.8em;
    }
  }

  .album-actions {
    @include media-breakpoint-down(xs) {
      display: flex;
      align-items: flex-end;
      margin: 1em 0 1em 0;
      justify-content: flex-end;
      a {
        margin-left: 1em;
      }
    }
  }

  .report-link {
    /deep/ svg {
      color: $steel;

      &:hover {
        color: white;
      }
    }
  }
</style>

<script>
import moment from '@/utils/moment'
import { mapGetters } from 'vuex'

import FontAwesomeIcon from '@fortawesome/vue-fontawesome'
import Comments from './Comments'
import Player from './Player'
import VideoCard from './VideoCard'
import VideoInfo from './VideoInfo'
import AlbumCard from './AlbumCard'
import AddToPlaylist from '@/components/widgets/AddToPlaylist'
import Pay from '@/components/widgets/Pay'
import BigEventCard from '@/components/widgets/BigEventCard'
import Report from '@/components/widgets/Report'
import LinkButton from '@/components/widgets/LinkButton'

import { EMPTY_IMAGE_16_9 } from '@/store/modules/tag'

import { formatText } from '@/utils/textformat'

export default {
  name: 'Feed',
  components: {
    FontAwesomeIcon,
    Comments,
    Player,
    VideoCard,
    VideoInfo,
    AddToPlaylist,
    BigEventCard,
    AlbumCard,
    Pay,
    Report,
    LinkButton
  },
  data () {
    return {
      currentVideo: null
    }
  },
  props: {
    feed: Array,
    artist: Object,
    canManage: Boolean
  },
  computed: {
    ...mapGetters('profile', ['currentUser'])
  },
  methods: {
    itemType (item) {
      if (item.song) return 'video'
      if (item.live_event) return 'live'
      if (item.album) return 'album'
      return 'text'
    },
    formatDate (item) {
      return moment(item.created_at).fromNow()
    },
    formatText,
    formatWhat (item) {
      if (this.itemType(item) === 'video') return 'published a video'
      if (this.itemType(item) === 'live') return 'announced a live stream'
      if (this.itemType(item) === 'album') return 'released an album'
      return 'posted a message'
    },
    formatVisibility (item) {
      if (item.visibility === 'subscribers') return '(subscribers only)'
      if (item.visibility === 'private') return '(private)'
    },
    getArtist (item) {
      return item.artist ? item.artist : (this.artist ? this.artist : {})
    },
    artistLink (item) {
      const artist = this.getArtist(item)
      return {
        name: 'artist-about',
        params: {
          artistId: artist.id
        }
      }
    },
    getThumbnail (item) {
      if (!item.song) return EMPTY_IMAGE_16_9
      if (!item.song.video_thumbnail) return EMPTY_IMAGE_16_9
      return item.song.video_thumbnail
    },
    playVideo (item) {
      if (!item.song) return
      this.$eventHub.$emit('stop-playback', this)
      this.currentVideo = item.song
    },
    async deleteUpdate (item) {
      if (!this.canManage) return
      await this.$store.dispatch('feed/deleteUpdate', { id: item.id })

      let idx = this.feed.indexOf(item)
      if (idx !== -1) this.feed.splice(idx, 1)

      this.$store.dispatch('flash/showMessage', {message: 'Update deleted'})
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/widgets/Feed.vue